import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  Calculator,
  DollarSign,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  BarChart3,
} from "lucide-react";
import Papa from "papaparse";
import {
  formatCurrency,
  extractAffiliateName,
  normalizeAdName,
} from "../utils/helpers";
import { DEFAULT_COMMISSION_PERCENTAGE, BRANDS } from "../utils/constants";
import {
  calculateAggregateCommission,
  calculateTieredCommission,
  getAllTiers,
} from "../utils/commissionTiers";
// Use the new consistent data hook
import useAffiliateData from "../hooks/useAffiliateData";
import Card from "./ui/Card";
import Button from "./ui/Button";
import Alert from "./ui/Alert";
import PayoutRequest from "./PayoutRequest";
import ReferralCenter from "./ReferralCenter";
import CreativeSubmissionInstructions from "./CreativeSubmissionInstructions";
import CommissionBreakdown from "./affiliate-details/commision-breakdown";
import DateRangePicker from "./DateRangePicker";

const AffiliateView = () => {
  const { affiliateName } = useParams();
  const decodedAffiliateName = decodeURIComponent(affiliateName);

  // Use the affiliate data hook
  const affiliateDataHook = useAffiliateData();
  const { getIndividualAffiliateData, isLoading, error, clearCacheData } =
    affiliateDataHook;

  // State variables
  const [supabaseData, setAffiliateData] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(BRANDS[0]?.value || "holistic_hercules");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 20;

  // Collapsible sections state
  const [isCommissionBreakdownExpanded, setIsCommissionBreakdownExpanded] =
    useState(false);
  const [isAdPerformanceExpanded, setIsAdPerformanceExpanded] = useState(true);

  // Load affiliate data when component mounts or filters change
  useEffect(() => {
    const loadAffiliateData = async () => {
      if (!decodedAffiliateName) return;

      try {
        setIsDataLoading(true);
        console.log(
          "🔍 AffiliateView: Loading data for:",
          decodedAffiliateName
        );

        // Clear any cached data to ensure fresh results
        if (clearCacheData) {
          clearCacheData();
        }

        // Use the optimized individual affiliate data function
        const data = await getIndividualAffiliateData(
          decodedAffiliateName,
          selectedBrand
        );

        console.log(
          "📊 AffiliateView: Loaded data:",
          data?.length || 0,
          "records"
        );
        setAffiliateData(data);
      } catch (err) {
        console.error("❌ AffiliateView: Error loading data:", err);
        setAffiliateData([]);
      } finally {
        setIsDataLoading(false);
      }
    };

    loadAffiliateData();
  }, [
    getIndividualAffiliateData,
    decodedAffiliateName,
    selectedBrand,
    selectedDate,
    clearCacheData,
  ]);

  // Convert affiliate data to the expected format for processing
  const csvDataFromSupabase = useMemo(() => {
    if (!supabaseData) {
      console.log("supabaseData is null/undefined");
      return null;
    }

    // The data is already in the correct format from useAffiliateData
    return supabaseData;
  }, [supabaseData]);

  // Filter data for this specific affiliate (already filtered by the hook)
  const affiliateData = useMemo(() => {
    if (!csvDataFromSupabase) {
      console.log("No csvDataFromSupabase");
      return [];
    }

    // CONSISTENCY FIX: Use the data as-is from the summary table
    // Don't re-process or recalculate - this should match the dashboard exactly
    console.log(
      "🔍 AffiliateView: Using pre-calculated data from summary table"
    );
    console.log("📊 Raw data sample:", csvDataFromSupabase.slice(0, 3));

    // Simply transform the data to the expected format without re-processing
    const filteredRows =
      selectedBrand && selectedBrand !== "all"
        ? csvDataFromSupabase.filter((row) => (row.Brand || "").toLowerCase() === selectedBrand.toLowerCase())
        : csvDataFromSupabase;

    const transformedData = filteredRows.map((row) => {
      return {
        "Ad Name": row["Ad Name"],
        "Ad Spend": parseFloat(row["Ad Spend"]) || 0,
        "Pixel CV": parseFloat(row["Pixel CV"]) || 0,
        "Pixel Purchases": parseInt(row["Pixel Purchases"]) || 0,
        ROAS: parseFloat(row.ROAS) || 0,
        Commission: parseFloat(row.Commission) || 0, // Use pre-calculated value
        CommissionTier: row.CommissionTier || "",
        CommissionLabel: row.CommissionLabel || "",
        "Campaign Name": row["Campaign Name"] || "",
        "Adset Name": row["Adset Name"] || "",
        CPA: parseFloat(row.CPA) || 0,
        AOV: parseFloat(row.AOV) || 0,
        Brand: row.Brand || "",
        Date: row.Date,
      };
    });

    // Sort by spend descending to show the highest spending ads first
    const sortedData = transformedData.sort(
      (a, b) => parseFloat(b["Ad Spend"]) - parseFloat(a["Ad Spend"])
    );

    console.log(
      "✅ AffiliateView: Transformed data sample:",
      sortedData.slice(0, 3)
    );
    return sortedData;
  }, [csvDataFromSupabase, selectedBrand]);

  // Calculate affiliate statistics
  const affiliateStats = useMemo(() => {
    if (!affiliateData || affiliateData.length === 0) return null;

    console.log(
      "🔍 AffiliateView: Calculating affiliate stats for",
      decodedAffiliateName
    );
    console.log("📊 Raw affiliate data sample:", affiliateData.slice(0, 3));

    const stats = affiliateData.reduce(
      (acc, row, index) => {
        const adSpend = parseFloat(row["Ad Spend"]) || 0;
        const revenue = parseFloat(row["Pixel CV"]) || 0;
        const purchases = parseFloat(row["Pixel Purchases"]) || 0;
        const commission = parseFloat(row["Commission"]) || 0;

        // Log details for debugging
        if (index < 5) {
          console.log(`  Ad ${index + 1}:`, {
            name: row["Ad Name"],
            spend: adSpend,
            commission: commission,
            runningTotal: acc.totalCommission + commission,
          });
        }

        return {
          totalAdSpend: acc.totalAdSpend + adSpend,
          totalRevenue: acc.totalRevenue + revenue,
          totalPurchases: acc.totalPurchases + purchases,
          totalCommission: acc.totalCommission + commission,
          adCount: acc.adCount + 1,
        };
      },
      {
        totalAdSpend: 0,
        totalRevenue: 0,
        totalPurchases: 0,
        totalCommission: 0,
        adCount: 0,
      }
    );

    console.log("✅ AffiliateView: Final calculated stats:", {
      totalCommission: stats.totalCommission,
      totalAdSpend: stats.totalAdSpend,
      adCount: stats.adCount,
    });

    stats.avgROAS =
      stats.totalAdSpend > 0 ? stats.totalRevenue / stats.totalAdSpend : 0;
    stats.avgCommissionRate =
      stats.totalAdSpend > 0 ? stats.totalCommission / stats.totalAdSpend : 0;

    return stats;
  }, [affiliateData, decodedAffiliateName]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!affiliateData) return [];

    if (!searchTerm.trim()) {
      return affiliateData;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return affiliateData.filter((row) =>
      row["Ad Name"].toLowerCase().includes(searchLower)
    );
  }, [affiliateData, searchTerm]);

  // Paginate the filtered data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, ROWS_PER_PAGE]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Export affiliate data to CSV
  const exportAffiliateData = useCallback(() => {
    if (!filteredData) return;

    const exportData = filteredData.map((row) => ({
      "Ad Name": row["Ad Name"],
      "Ad Spend": `$${parseFloat(row["Ad Spend"]).toFixed(2)}`,
      ROAS: parseFloat(row["ROAS"] || 0).toFixed(2),
      "Commission Tier": row["CommissionLabel"] || "No tier",
      Purchases: row["Pixel Purchases"] || 0,
      Commission: `$${parseFloat(row["Commission"]).toFixed(2)}`,
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${decodedAffiliateName}-performance-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredData, decodedAffiliateName]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-500/10 to-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-orange-600/10 to-red-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>

        {/* Noise texture overlay */}
        <div className="absolute inset-0 bg-black/30 mix-blend-multiply pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
          <div className="text-center">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-white/20 border-t-[#B8460E] rounded-full animate-spin backdrop-blur-sm mx-auto"></div>
              <div
                className="absolute inset-0 w-20 h-20 border-4 border-transparent border-t-[#8B3A0E] rounded-full animate-spin mx-auto"
                style={{ animationDelay: "0.3s", animationDuration: "1.5s" }}
              ></div>
              <div className="absolute inset-0 w-20 h-20 rounded-full bg-[#B8460E]/20 blur-xl animate-pulse mx-auto"></div>
            </div>
            <p className="mt-4 text-gray-300">Loading affiliate data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-500/10 to-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-orange-600/10 to-red-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>

        {/* Noise texture overlay */}
        <div className="absolute inset-0 bg-black/30 mix-blend-multiply pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
          <Alert type="error" message={error} />
        </div>
      </div>
    );
  }

  if (!affiliateStats || affiliateData.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-500/10 to-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-orange-600/10 to-red-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>

        {/* Noise texture overlay */}
        <div className="absolute inset-0 bg-black/30 mix-blend-multiply pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-6">
              <Link
                to="/dashboard"
                className="inline-flex items-center text-[#B8460E] hover:text-[#8B3A0E] transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Dashboard
              </Link>
            </div>

            <Card className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                No Data Found for {decodedAffiliateName}
              </h2>
              <p className="text-gray-600">
                There is no performance data available for this affiliate.
              </p>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-500/10 to-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-orange-600/10 to-red-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      {/* Noise texture overlay */}
      <div className="absolute inset-0 bg-black/30 mix-blend-multiply pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-[#B8460E] hover:text-[#8B3A0E] transition-colors mb-6"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                {decodedAffiliateName}
              </h1>
              <p className="text-lg text-gray-300">
                Performance Overview & Analytics
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={exportAffiliateData}
              className="shadow-sm self-start lg:self-auto bg-[#B8460E] hover:bg-[#8B3A0E] text-white border-[#B8460E]"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Data
            </Button>
          </div>

          {/* Date and Brand Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <DateRangePicker
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
              />
            </div>
            <div className="flex-1 sm:flex-none sm:w-48">
              <div className="relative">
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full px-4 py-2 bg-black/40 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent hover:bg-black/50 transition-colors appearance-none cursor-pointer"
                >
                  {BRANDS.map((brand) => (
                    <option
                      key={brand.value}
                      value={brand.value}
                      className="bg-gray-900 text-white"
                    >
                      {brand.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics - Minimal Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="p-4 text-center bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-xl">
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">
              Ad Spend
            </div>
            <div className="text-lg font-bold text-white">
              {formatCurrency(affiliateStats.totalAdSpend)}
            </div>
          </div>

          <div className="p-4 text-center bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-xl">
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">
              Avg ROAS
            </div>
            <div className="text-lg font-bold text-white">
              {affiliateStats.avgROAS.toFixed(2)}
            </div>
          </div>

          <div className="p-4 text-center bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-xl">
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">
              Purchases
            </div>
            <div className="text-lg font-bold text-white">
              {affiliateStats.totalPurchases}
            </div>
          </div>

          <div className="p-4 text-center bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-xl">
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">
              Commission
            </div>
            <div className="text-lg font-bold text-[#B8460E]">
              {formatCurrency(affiliateStats.totalCommission)}
            </div>
          </div>
        </div>

        {/* Ad Performance Table - Collapsible */}
        <div className="overflow-hidden bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-xl">
          <button
            onClick={() => setIsAdPerformanceExpanded(!isAdPerformanceExpanded)}
            className="w-full px-4 py-3 bg-white/5 hover:bg-white/10 transition-colors duration-200 border-b border-white/10"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <BarChart3 className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-white">
                  Ad Performance Details
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-semibold text-gray-300">
                  {affiliateData.length} ads
                </span>
                {isAdPerformanceExpanded ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </div>
          </button>

          {isAdPerformanceExpanded && (
            <>
              <div className="px-6 py-4 bg-white/5 border-b border-white/10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-sm text-gray-300 font-medium">
                      {`Top ${Math.min(
                        ROWS_PER_PAGE,
                        filteredData.length
                      )} of ${filteredData.length} ads (by spend)`}
                      {searchTerm &&
                        ` (filtered from ${affiliateData.length} total)`}
                    </p>
                  </div>

                  {/* Search Bar */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search ads..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-[#B8460E] focus:border-[#B8460E] sm:text-sm text-white placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gradient-to-b from-white/5 to-transparent border-b border-white/10">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Ad Name
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Ad Spend
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        ROAS
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Tier
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Purchases
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Commission
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-transparent divide-y divide-white/10">
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row, index) => {
                        // Calculate global index for medals (accounts for pagination)
                        const globalIndex =
                          (currentPage - 1) * ROWS_PER_PAGE + index;

                        // Medal logic for top 3 ads globally
                        const getMedal = (globalIndex) => {
                          if (globalIndex === 0) return "🥇"; // Gold
                          if (globalIndex === 1) return "🥈"; // Silver
                          if (globalIndex === 2) return "🥉"; // Bronze
                          return null;
                        };

                        const medal = getMedal(globalIndex);

                        return (
                          <tr
                            key={index}
                            className="hover:bg-white/5 transition-colors"
                          >
                            <td className="px-6 py-4 text-sm font-medium text-white">
                              <div className="flex items-center space-x-2">
                                {medal && (
                                  <span className="text-lg">{medal}</span>
                                )}
                                <div
                                  className="max-w-xs truncate"
                                  title={row["Ad Name"]}
                                >
                                  {row["Ad Name"]}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                              {formatCurrency(parseFloat(row["Ad Spend"]) || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                              {parseFloat(row["ROAS"] || 0).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {row["CommissionLabel"] ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#B8460E]/20 text-[#B8460E] border border-[#B8460E]/30">
                                  {row["CommissionLabel"]}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/30">
                                  N/A
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-right">
                              {parseFloat(row["Pixel Purchases"] || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-[#B8460E] font-semibold text-right">
                              {formatCurrency(
                                parseFloat(row["Commission"]) || 0
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-8 text-center">
                          <div className="text-gray-400">
                            {searchTerm
                              ? "No ads found matching your search"
                              : "No ads available"}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Creative Submission Instructions */}
        <div className="mb-6 mt-6">
          <CreativeSubmissionInstructions />
        </div>

        {/* Commission Breakdown - Collapsible */}
        {affiliateData.length > 0 && (
          <CommissionBreakdown
            setIsCommissionBreakdownExpanded={setIsCommissionBreakdownExpanded}
            isCommissionBreakdownExpanded={isCommissionBreakdownExpanded}
            affiliateStats={affiliateStats}
            affiliateData={affiliateData}
          />
        )}

        {/* Payout Request Section */}
        {affiliateData.length > 0 && (
          <div className="mb-6">
            <PayoutRequest
              affiliateName={decodedAffiliateName}
              adData={supabaseData || []}
              processedAffiliateData={affiliateData}
              totalCommission={affiliateStats.totalCommission}
              onSubmitRequest={(requestData) => {
                console.log("Payout request received:", requestData);
                alert(
                  `Payout request submitted for ${formatCurrency(
                    requestData.totalAmount
                  )} to ${requestData.paypalEmail}`
                );
              }}
            />
          </div>
        )}

        {/* Referral Center Section */}
        {affiliateData.length > 0 && (
          <div className="mb-6">
            <ReferralCenter affiliateName={decodedAffiliateName} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateView;
